import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { ToWords } from 'to-words';
import { FinanceService } from 'src/app/services/finance.service';
import { MaterialModule } from 'src/app/shared/material.module';

pdfMake.vfs = pdfFonts.vfs;
const toWords = new ToWords();

@Component({
  selector: 'app-receipt-print',
  standalone: true,
  imports: [CommonModule, MaterialModule, MatProgressBar],
  templateUrl: './receipt-print.component.html',
  styleUrl: './receipt-print.component.scss'
})
export class ReceiptPrintComponent implements OnInit, OnDestroy {
  receiptId!: number;
  receiptData: any;
  previewData: any = null;
  pdfDoc: any;
  isDialogMode = false;
  includeSignature = false;
  showVoucherSection = false;
  private originalHtmlOverflow = '';
  private originalBodyOverflow = '';

  constructor(
    private route: ActivatedRoute,
    private financeService: FinanceService,
    private snackBar: MatSnackBar,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { id?: number } | null,
    @Optional() private dialogRef: MatDialogRef<ReceiptPrintComponent> | null
  ) {}

  ngOnInit(): void {
    document.body.classList.add('invoice-preview-mode');
    this.originalHtmlOverflow = document.documentElement.style.overflow;
    this.originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    this.isDialogMode = !!this.dialogData?.id;
    this.receiptId = Number(this.dialogData?.id ?? this.route.snapshot.paramMap.get('id'));
    if (this.receiptId) {
      this.loadReceiptData();
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('invoice-preview-mode');
    document.documentElement.style.overflow = this.originalHtmlOverflow;
    document.body.style.overflow = this.originalBodyOverflow;
  }

  printPdf() {
    this.pdfDoc?.print();
  }

  downloadPdf() {
    this.pdfDoc?.download(`Receipt-Advice-${this.previewData?.voucherNo || this.receiptId}.pdf`);
  }

  sendMail() {
    this.snackBar.open('Receipt mail template configured nahi mila. Mail compose open nahi kiya gaya.', 'Close', {
      duration: 4000
    });
  }

  onSignatureToggle(checked: boolean) {
    if (this.includeSignature === checked) {
      return;
    }

    this.includeSignature = checked;
    this.generatePdf();
  }

  onVoucherSectionToggle(checked: boolean) {
    if (this.showVoucherSection === checked) {
      return;
    }

    this.showVoucherSection = checked;
    this.generatePdf();
  }

  closePreview() {
    this.dialogRef?.close();
  }

  private loadReceiptData(): void {
    this.financeService.getReceiptPrintById(this.receiptId).subscribe({
      next: (data: any) => {
        this.receiptData = Array.isArray(data) ? data[0] : data;
        this.previewData = this.buildPreviewData(this.receiptData || {});
        this.generatePdf();
      },
      error: (err) => console.error('Error loading receipt print', err)
    });
  }

  private buildPreviewData(source: any) {
    const header = source.header || {};
    const company = this.pickEntity(source, ['company', 'companymaster', 'companydetails']);
    const client = this.pickEntity(source, ['client', 'party', 'customerdetails']);
    const branch = this.pickEntity(source, ['branch']);
    const rawSubdetails = this.asArray(source.voucherSubdetails);
    const billRowsRaw = rawSubdetails.filter((row: any) => row?.ismonthlysales !== false);
    const advanceRowsRaw = rawSubdetails.filter((row: any) => row?.ismonthlysales === false);
    const deductionRowsRaw = billRowsRaw.flatMap((row: any) =>
      this.asArray(row.taxcharges).map((charge: any) => ({
        billNo: this.safeValue(row.billno, '-'),
        branchName: this.safeValue(row.branchname, '-'),
        ledgerName: this.safeValue(charge.ledgername || charge.ledger || charge.accountname, '-'),
        amount: Number(charge.amount || 0),
        remarks: this.safeValue(charge.remarks, '-')
      }))
    );
    const voucherDetailsRaw = this.asArray(source.voucherDetails);

    const billRows = billRowsRaw.map((row: any, index: number) => {
      const otherDeduction = Number(row.otherdeductiontotal || this.sumAmounts(row.taxcharges, 'amount'));
      const receiptSettlement = Number(row.amountpaid || 0);
      const tds = Number(row.tdsbillamount || row.tds || 0);
      const gross = Number(row.netbillamt || row.billamt || 0);
      const netReceived = Math.max(receiptSettlement - otherDeduction, 0);

      return {
        sr: index + 1,
        branchName: this.safeValue(row.branchname, '-'),
        billNo: this.safeValue(row.billno, '-'),
        billDate: this.formatDateValue(row.billdate),
        grossRaw: gross,
        receiptSettlementRaw: receiptSettlement,
        tdsRaw: tds,
        otherDeductionRaw: otherDeduction,
        netReceivedRaw: netReceived,
        grossAmount: this.formatCurrency(gross, true),
        receiptSettlementAmount: this.formatCurrency(receiptSettlement, true),
        tdsAmount: this.formatCurrency(tds, true),
        otherDeductionAmount: this.formatCurrency(otherDeduction, true),
        netReceivedAmount: this.formatCurrency(netReceived, true)
      };
    });

    const deductionRows = deductionRowsRaw.map((row: any, index: number) => ({
      sr: index + 1,
      billNo: row.billNo,
      branchName: row.branchName,
      ledgerName: row.ledgerName,
      amount: this.formatCurrency(row.amount, true),
      remarks: row.remarks
    }));

    const voucherRows = voucherDetailsRaw.map((row: any, index: number) => ({
      sr: index + 1,
      ledgerName: this.safeValue(row.ledgername || row.accountname || row.name, '-'),
      debitAmount: this.formatCurrency(row.debitamount || row.dramt || 0, true),
      creditAmount: this.formatCurrency(row.creditamount || row.cramt || 0, true),
      remarks: this.safeValue(row.remarks, '-')
    }));

    const finalReceiptAmount = Number(header.paidamount || 0);
    const totalTds = Number(header.tdsamt || 0);
    const totalOtherDeductions = this.sumRaw(deductionRowsRaw, 'amount');
    const totalBillReceipt = billRows.reduce((sum: number, row: any) => sum + row.receiptSettlementRaw, 0);
    const openAdvance = Number(header.openadvance || 0);
    const billGrossTotal = billRows.reduce((sum: number, row: any) => sum + row.grossRaw, 0);
    const billReceiptSettlementTotal = billRows.reduce((sum: number, row: any) => sum + row.receiptSettlementRaw, 0);
    const billTdsTotal = billRows.reduce((sum: number, row: any) => sum + row.tdsRaw, 0);
    const billOtherDeductionTotal = billRows.reduce((sum: number, row: any) => sum + row.otherDeductionRaw, 0);
    const billNetReceivedTotal = billRows.reduce((sum: number, row: any) => sum + row.netReceivedRaw, 0);
    const advanceTds = Math.max(totalTds - billTdsTotal, 0);
    const advanceNetReceipt = Math.max(openAdvance - advanceTds, 0);
    const roundOffAmount = Number(header.roundoffamount || 0);
    const advanceSectionRows = openAdvance > 0 || advanceTds > 0
      ? [{
          sr: 1,
          description: 'Advance Adjustment',
          advanceAmount: this.formatCurrency(openAdvance, true),
          advanceTds: this.formatCurrency(advanceTds, true),
          advanceReceipt: this.formatCurrency(advanceNetReceipt, true)
        }]
      : [];

    return {
      logo: this.resolveImage([
        company.logoBase64,
        company.logobase64,
        company.logo,
        source.logoBase64,
        source.logobase64
      ]),
      signatureImage: this.resolveImage([
        company.signature,
        company.signatureBase64,
        company.signaturebase64,
        source.signature,
        source.signatureBase64
      ]),
      stampImage: this.resolveImage([
        company.stamp,
        company.stampBase64,
        company.stampbase64,
        source.stamp,
        source.stampBase64
      ]),
      companyInitials: this.getInitials(company.name),
      companyName: this.safeValue(company.name, 'VCS ERP'),
      companyNameTitle: this.safeValue(company.name, 'VCS ERP'),
      companyAddress: this.joinAddress([company.address1, company.address2, company.city, company.state, company.country, company.pincode]),
      companyMeta: this.buildCompanyMeta(company),
      signatoryName: this.safeValue(
        company.authorized_signatory_name ||
        company.authorizedSignatoryName ||
        source.authorized_signatory_name ||
        source.authorizedSignatoryName,
        ''
      ),
      signatoryTitle: this.safeValue(
        company.authorized_signatory_title ||
        company.authorizedSignatoryTitle ||
        source.authorized_signatory_title ||
        source.authorizedSignatoryTitle,
        'Authorized Signatory'
      ),
      voucherNo: this.safeValue(header.voucherno || header.code),
      voucherDate: this.formatDateValue(header.voucherdate),
      paymentMode: this.safeValue(header.paymentmodename || header.paymentmode || header.paymentmodeid, '-'),
      receiptType: this.safeValue(header.paymentvouchertypename || header.paymentvouchertype || header.paymentvouchertypeid, '-'),
      chequeNo: this.safeValue(header.chequeno, '-'),
      chequeDate: this.formatDateValue(header.chequedate),
      clientName: this.safeValue(client.name || client.clientname, '-'),
      clientAddress: this.joinAddress([client.address1, client.address2, client.city, client.state, client.country, client.pincode]),
      clientMeta: [
        this.metaLine('GSTIN', client.gstin || client.gstnumber),
        this.metaLine('PAN', client.pan || client.panno),
        this.metaLine('Phone', client.mobile || client.phone),
        this.metaLine('Email', client.email || client.emailid)
      ].filter(Boolean),
      branchName: this.safeValue(branch.branchname, '-'),
      bankName: this.safeValue(header.paytobankname || header.bankname, '-'),
      bankAccountNo: this.safeValue(header.paytobankaccountnumber || header.accountno, '-'),
      bankIfsc: this.safeValue(header.paytobankifsccode || header.ifsccode, '-'),
      bankBranch: this.safeValue(header.paytobankbranch || header.bankbranch, '-'),
      overview: [
        { label: 'Client', value: this.safeValue(client.name || client.clientname, '-') },
        { label: 'Receipt Mode', value: this.safeValue(header.paymentmodename || header.paymentmode || header.paymentmodeid, '-') },
        { label: 'Total TDS', value: this.formatCurrency(totalTds, true) },
        { label: 'Net Receipt', value: this.formatCurrency(finalReceiptAmount, true) }
      ],
      hasBillRows: billRows.length > 0,
      hasAdvanceSection: advanceSectionRows.length > 0,
      hasDeductionRows: deductionRows.length > 0,
      hasVoucherRows: voucherRows.length > 0,
      billRows,
      advanceSectionRows,
      deductionRows,
      voucherRows,
      billGrossTotal: this.formatCurrency(billGrossTotal, true),
      billReceiptSettlementTotal: this.formatCurrency(billReceiptSettlementTotal, true),
      billTdsTotal: this.formatCurrency(billTdsTotal, true),
      billOtherDeductionTotal: this.formatCurrency(billOtherDeductionTotal, true),
      billNetReceivedTotal: this.formatCurrency(billNetReceivedTotal, true),
      advanceNetReceipt: this.formatCurrency(advanceNetReceipt, true),
      totalBillReceipt: this.formatCurrency(totalBillReceipt, true),
      totalTds: this.formatCurrency(totalTds, true),
      totalOtherDeductions: this.formatCurrency(totalOtherDeductions, true),
      openAdvance: this.formatCurrency(openAdvance, true),
      roundOffAmount: this.formatCurrency(roundOffAmount, true),
      finalReceiptAmount: this.formatCurrency(finalReceiptAmount, true),
      amountInWords: `${toWords.convert(finalReceiptAmount, { currency: true })}/-`,
      remarks: this.safeValue(header.remarks, '-')
    };
  }

  private generatePdf() {
    if (!this.previewData) {
      return;
    }

    const billBody = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'BRANCH', style: 'tableHeader' },
        { text: 'BILL NO', style: 'tableHeader' },
        { text: 'BILL DATE', style: 'tableHeader' },
        { text: 'GROSS', style: 'tableHeader' },
        { text: 'RECEIPT AMOUNT', style: 'tableHeader' },
        { text: 'TDS', style: 'tableHeader' },
        { text: 'OTHER DED.', style: 'tableHeader' },
        { text: 'NET RECEIVED', style: 'tableHeader' }
      ],
      ...this.previewData.billRows.map((row: any) => ([
        { text: String(row.sr), alignment: 'center' },
        { text: row.branchName },
        { text: row.billNo },
        { text: row.billDate, alignment: 'center' },
        { text: row.grossAmount, alignment: 'right' },
        { text: row.receiptSettlementAmount, alignment: 'right' },
        { text: row.tdsAmount, alignment: 'right' },
        { text: row.otherDeductionAmount, alignment: 'right' },
        { text: row.netReceivedAmount, alignment: 'right', bold: true }
      ])),
      ...(this.previewData.hasBillRows ? [[
        { text: 'BILL SUMMARY', colSpan: 4, alignment: 'right', bold: true, fillColor: '#eef2ff', margin: [0, 4, 8, 4] },
        {}, {}, {},
        { text: this.previewData.billGrossTotal, alignment: 'right', fillColor: '#eef2ff', bold: true },
        { text: this.previewData.billReceiptSettlementTotal, alignment: 'right', fillColor: '#eef2ff', bold: true },
        { text: this.previewData.billTdsTotal, alignment: 'right', fillColor: '#eef2ff', bold: true },
        { text: this.previewData.billOtherDeductionTotal, alignment: 'right', fillColor: '#eef2ff', bold: true },
        { text: this.previewData.billNetReceivedTotal, alignment: 'right', fillColor: '#eef2ff', bold: true }
      ]] : [])
    ];

    const advanceBody = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'DESCRIPTION', style: 'tableHeader' },
        { text: 'ADVANCE AMOUNT', style: 'tableHeader' },
        { text: 'ADVANCE TDS', style: 'tableHeader' },
        { text: 'ADVANCE RECEIPT', style: 'tableHeader' }
      ],
      ...this.previewData.advanceSectionRows.map((row: any) => ([
        { text: String(row.sr), alignment: 'center' },
        { text: row.description },
        { text: row.advanceAmount, alignment: 'right' },
        { text: row.advanceTds, alignment: 'right' },
        { text: row.advanceReceipt, alignment: 'right', bold: true }
      ]))
    ];

    const deductionBody = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'BILL NO', style: 'tableHeader' },
        { text: 'BRANCH', style: 'tableHeader' },
        { text: 'LEDGER', style: 'tableHeader' },
        { text: 'AMOUNT', style: 'tableHeader' },
        { text: 'REMARKS', style: 'tableHeader' }
      ],
      ...this.previewData.deductionRows.map((row: any) => ([
        { text: String(row.sr), alignment: 'center' },
        { text: row.billNo },
        { text: row.branchName },
        { text: row.ledgerName },
        { text: row.amount, alignment: 'right', bold: true },
        { text: row.remarks }
      ]))
    ];

    const voucherBody = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'LEDGER', style: 'tableHeader' },
        { text: 'DEBIT', style: 'tableHeader' },
        { text: 'CREDIT', style: 'tableHeader' },
        { text: 'REMARKS', style: 'tableHeader' }
      ],
      ...this.previewData.voucherRows.map((row: any) => ([
        { text: String(row.sr), alignment: 'center' },
        { text: row.ledgerName },
        { text: row.debitAmount, alignment: 'right' },
        { text: row.creditAmount, alignment: 'right' },
        { text: row.remarks }
      ]))
    ];

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [20, 12, 20, 42],
      footer: (currentPage: number, pageCount: number) => ({
        margin: [20, 0, 20, 6],
        stack: [
          {
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 555, y2: 0, lineWidth: 1, color: '#8da0b8' },
              { type: 'rect', x: 0, y: 5, w: 132, h: 2.6, color: '#153a6b' },
              { type: 'rect', x: 441, y: 5, w: 114, h: 2.6, color: '#d97706' }
            ]
          },
          {
            margin: [0, 6, 0, 0],
            columns: [
              { width: '*', text: 'Generated using VCS ERP', style: 'footerText' },
              { width: 90, text: `Page ${currentPage} of ${pageCount}`, style: 'footerPageText', alignment: 'right' }
            ]
          }
        ]
      }),
      background: this.previewData.logo
        ? { image: this.previewData.logo, alignment: 'center', width: 300, height: 300, opacity: 0.05, marginTop: 220 }
        : undefined,
      content: [
        {
          canvas: [
            { type: 'rect', x: 0, y: 0, w: 551, h: 6, color: '#153a6b' },
            { type: 'rect', x: 405, y: 0, w: 146, h: 6, color: '#d97706' },
            { type: 'line', x1: 0, y1: 15, x2: 551, y2: 15, lineWidth: 0.7, color: '#e2e8f0' }
          ],
          margin: [0, -10, 0, 4]
        },
        {
          table: {
            widths: [74, '*', 165],
            body: [[
              {
                stack: this.previewData.logo
                  ? [{ image: this.previewData.logo, width: 66, fit: [66, 66], alignment: 'center' }]
                  : [{ text: this.previewData.companyInitials, style: 'logoFallback' }],
                border: [false, false, false, false],
                margin: [0, 6, 0, 0]
              },
              {
                stack: [
                  { text: this.previewData.companyName.toUpperCase(), style: 'companyHeader' },
                  this.previewData.companyAddress ? { text: this.previewData.companyAddress, style: 'companyMeta' } : '',
                  ...this.previewData.companyMeta.map((text: string) => ({ text, style: 'companyMeta' }))
                ].filter(Boolean),
                border: [false, false, false, false],
                margin: [0, 4, 0, 0]
              },
              {
                stack: [
                  { text: 'RECEIPT ADVICE', style: 'invoiceHeaderBadge' },
                  { text: `Receipt No: ${this.previewData.voucherNo}`, style: 'invoiceMetaStrong' },
                  { text: `Receipt Date: ${this.previewData.voucherDate}`, style: 'invoiceMeta' },
                  { text: `Receipt Type: ${this.previewData.receiptType}`, style: 'invoiceMeta' },
                  { text: `Mode: ${this.previewData.paymentMode}`, style: 'invoiceMeta' }
                ],
                fillColor: '#f6f8ff',
                margin: [10, 8, 10, 8],
                border: [true, true, true, true]
              }
            ]]
          },
          layout: {
            hLineColor: () => '#d7deea',
            vLineColor: () => '#d7deea'
          }
        },
        {
          columns: this.previewData.overview.map((card: any) => ({
            width: '*',
            stack: [
              { text: card.label, style: 'cardTitle' },
              { text: card.value, style: 'cardHeading' }
            ],
            margin: [8, 8, 8, 8],
            fillColor: '#f8fafc'
          })),
          columnGap: 12,
          margin: [0, 10, 0, 8]
        },
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Client Details', style: 'cardTitle' },
                { text: this.previewData.clientName, style: 'cardHeading' },
                ...(this.previewData.clientAddress ? [{ text: this.previewData.clientAddress, style: 'cardText' }] : []),
                ...this.previewData.clientMeta.map((text: string) => ({ text, style: 'cardText' }))
              ],
              margin: [8, 8, 8, 8],
              fillColor: '#f8fafc'
            },
            {
              width: '*',
              stack: [
                { text: 'Receipt Instructions', style: 'cardTitle' },
                { text: `Branch: ${this.previewData.branchName}`, style: 'cardText' },
                { text: `Bank: ${this.previewData.bankName}`, style: 'cardText' },
                { text: `A/C No: ${this.previewData.bankAccountNo}`, style: 'cardText' },
                { text: `IFSC: ${this.previewData.bankIfsc}`, style: 'cardText' },
                { text: `Bank Branch: ${this.previewData.bankBranch}`, style: 'cardText' },
                { text: `Cheque No: ${this.previewData.chequeNo}`, style: 'cardText' },
                { text: `Cheque Date: ${this.previewData.chequeDate}`, style: 'cardText' }
              ],
              margin: [8, 8, 8, 8],
              fillColor: '#f8fafc'
            }
          ],
          columnGap: 14,
          margin: [0, 0, 0, 8]
        },
        ...(this.previewData.hasBillRows ? [
          { text: 'Bill-wise Receipt Advice', style: 'sectionHeader' },
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: [22, 52, 58, 50, 56, 64, 40, 48, 56],
              body: billBody
            },
            layout: this.buildTableLayout()
          }
        ] : []),
        ...(this.previewData.hasAdvanceSection ? [
          { text: 'Advance Summary', style: 'sectionHeader', margin: [0, 10, 0, 0] },
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: [24, '*', 82, 74, 86],
              body: advanceBody
            },
            layout: this.buildTableLayout()
          }
        ] : []),
        ...(this.previewData.hasDeductionRows ? [
          { text: 'Other Deductions / Settlement Adjustments', style: 'sectionHeader', margin: [0, 10, 0, 0] },
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: [24, 58, 54, 118, 56, '*'],
              body: deductionBody
            },
            layout: this.buildTableLayout()
          }
        ] : []),
        ...(this.showVoucherSection && this.previewData.hasVoucherRows ? [
          { text: 'Voucher Ledger Summary', style: 'sectionHeader', margin: [0, 10, 0, 0] },
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: [24, '*', 68, 68, 110],
              body: voucherBody
            },
            layout: this.buildTableLayout()
          }
        ] : []),
        {
          margin: [0, 10, 0, 0],
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Amount In Words', style: 'sectionHeader' },
                { text: this.previewData.amountInWords, style: 'cardText' },
                ...(this.previewData.remarks !== '-' ? [
                  { text: 'Remarks', style: 'sectionHeader', margin: [0, 12, 0, 0] },
                  { text: this.previewData.remarks, style: 'cardText' }
                ] : [])
              ]
            },
            {
              width: 188,
              unbreakable: true,
              stack: [
                {
                  margin: [0, 50, 0, 0],
                  unbreakable: true,
                  stack: [
                    { text: `For ${this.previewData.companyNameTitle}`, alignment: 'right', bold: true, color: '#153a6b' },
                    ...(this.includeSignature
                      ? [{
                          columns: [
                            { width: '*', text: '' },
                            {
                              width: 'auto',
                              stack: [
                                {
                                  columns: [
                                    ...(this.previewData.stampImage ? [{ width: 70, image: this.previewData.stampImage, fit: [70, 70], margin: [0, 4, 6, 0] }] : []),
                                    ...(this.previewData.signatureImage ? [{ width: 74, image: this.previewData.signatureImage, fit: [74, 34], margin: [0, 22, 0, 0] }] : [])
                                  ]
                                }
                              ]
                            }
                          ],
                          margin: [0, 8, 0, 0]
                        }]
                      : []),
                    { text: this.previewData.signatoryName || '', alignment: 'right', margin: [0, 12, 0, 0], fontSize: 8.6, bold: true },
                    { text: this.previewData.signatoryTitle || 'Authorized Signatory', alignment: 'right', fontSize: 8.6, color: '#475569' }
                  ]
                }
              ]
            }
          ]
        }
      ],
      styles: {
        companyHeader: { fontSize: 18, bold: true, color: '#0f2852', alignment: 'center' },
        companyMeta: { fontSize: 10.2, color: '#2b3e5f', alignment: 'center', margin: [0, 1, 0, 0] },
        invoiceHeaderBadge: { fontSize: 11, bold: true, color: '#0f2852', margin: [0, 0, 0, 8] },
        invoiceMetaStrong: { fontSize: 9.4, bold: true, color: '#12253f', margin: [0, 2, 0, 0] },
        invoiceMeta: { fontSize: 9, color: '#334155', margin: [0, 2, 0, 0] },
        cardTitle: { fontSize: 9, bold: true, color: '#48627f', margin: [0, 0, 0, 6] },
        cardHeading: { fontSize: 12, bold: true, color: '#0f2852' },
        cardText: { fontSize: 9.1, color: '#334155', margin: [0, 2, 0, 0] },
        tableHeader: { fontSize: 8.7, bold: true, color: '#ffffff', fillColor: '#17354d', alignment: 'center', margin: [0, 4, 0, 4] },
        sectionHeader: { fontSize: 11.2, bold: true, color: '#0b7285', margin: [0, 0, 0, 6] },
        footerText: { fontSize: 8, color: '#64748b' },
        footerPageText: { fontSize: 8, color: '#334155', bold: true },
        logoFallback: {
          fontSize: 26,
          bold: true,
          color: '#153a6b',
          alignment: 'center',
          margin: [0, 16, 0, 0]
        }
      },
      defaultStyle: {
        fontSize: 9,
        color: '#0f172a'
      }
    };

    this.pdfDoc = pdfMake.createPdf(docDefinition);
  }

  private buildTableLayout() {
    return {
      hLineColor: () => '#b9c7d8',
      vLineColor: () => '#b9c7d8',
      hLineWidth: () => 0.7,
      vLineWidth: () => 0.7,
      fillColor: (rowIndex: number) => rowIndex === 0 ? '#17354d' : rowIndex % 2 === 0 ? '#f8fafc' : null
    };
  }

  private pickEntity(source: any, keys: string[]) {
    for (const key of keys) {
      if (source?.[key]) {
        return source[key];
      }
    }
    return {};
  }

  private buildCompanyMeta(company: any): string[] {
    const gstLine = this.metaLine('GSTIN', company.gstin || company.gstnumber, '');
    const panLine = this.metaLine('PAN', company.pan || company.panno, '');
    const emailLine = this.metaLine('Email', company.email || company.emailid, '');
    const websiteLine = this.metaLine('Website', company.website, '');

    return [
      [gstLine, panLine].filter(Boolean).join('    '),
      [emailLine, websiteLine].filter(Boolean).join('    ')
    ].filter(Boolean);
  }

  private metaLine(label: string, value: any, fallback = ''): string {
    const cleaned = this.safeValue(value, '');
    return cleaned ? `${label}: ${cleaned}` : fallback;
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '-';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.safeValue(value, '-');
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  private formatCurrency(value: any, showSymbol = true): string {
    const amount = Number(value || 0);
    const formatter = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return showSymbol ? `₹ ${formatter.format(amount)}` : formatter.format(amount);
  }

  private joinAddress(parts: any[]): string {
    return parts
      .map((part) => this.safeValue(part, ''))
      .filter(Boolean)
      .join(', ');
  }

  private getInitials(name: string): string {
    return this.safeValue(name, 'VC')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('');
  }

  private resolveImage(candidates: any[]): string | null {
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'string') {
        continue;
      }

      const trimmed = candidate.trim();
      if (!trimmed) {
        continue;
      }

      if (trimmed.startsWith('data:image')) {
        return trimmed;
      }

      return `data:image/png;base64,${trimmed}`;
    }

    return null;
  }

  private safeValue(value: any, fallback = '-'): string {
    if (value === null || value === undefined) {
      return fallback;
    }

    const text = String(value).trim();
    return text ? text : fallback;
  }

  private asArray(value: any): any[] {
    return Array.isArray(value) ? value : [];
  }

  private sumAmounts(rows: any, key: string): number {
    return this.asArray(rows).reduce((sum, row) => sum + Number(row?.[key] || 0), 0);
  }

  private sumRaw(rows: any[], key: string): number {
    return this.asArray(rows).reduce((sum, row) => sum + Number(row?.[key] || 0), 0);
  }
}
